import * as React from "react";
import { ConditionBuilderProvider } from "@/components/ConditionBuilder/context/ConditionBuilderContext";
import { ConditionBuilderConfig } from "@/components/ConditionBuilder/types";
import ConditionGroup from "./ConditionGroup";
import "./conditionBuilder.scss";

export interface ConditionBuilderProps {
  /** RHF field name holding the rules array (e.g. "serverRules"). */
  name: string;
  /**
   * RHF field name holding the AND/OR combinator (e.g. "serverRulesCombinator").
   * Required when the array name doesn't follow the `.conditions` →
   * `.combinator` convention internally derived for nested groups.
   */
  combinatorName: string;
  /**
   * Builder configuration. Pass attributes, operators, and behavior flags.
   */
  config: ConditionBuilderConfig;
}

/**
 * Top-level ConditionBuilder. RHF-native: reads/writes the rules array and
 * combinator field directly through react-hook-form.
 *
 * Form data shape expected in the parent form:
 *   {
 *     [combinatorName]: { id: 'AND' | 'OR', name: string },
 *     [name]: ConditionEntry[],
 *   }
 *
 * Nested groups internally use `${name}.${index}.conditions` and
 * `${name}.${index}.combinator` paths.
 *
 * Example:
 *   <ConditionBuilder
 *     name="serverRules"
 *     combinatorName="serverRulesCombinator"
 *     config={mcpRulesConfig}
 *   />
 */
const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  name,
  combinatorName,
  config,
}) => {
  return (
    <ConditionBuilderProvider value={{ config }}>
      <div className="condition-builder">
        <ConditionGroup name={name} combinatorName={combinatorName} depth={0} />
      </div>
    </ConditionBuilderProvider>
  );
};

export default ConditionBuilder;
